import React from 'react';
import { BiSolidImageAdd, BiX } from 'react-icons/bi';

import { PSInput } from '@/components/ui/input';
import ProBadge from '@/components/ui/ProBadge';
import { WrappedErrorBoundary } from '@/pages/WrappedErrorBoundary';

interface AvatarSelectorProps {
  t: (key: string) => string;
  permissions: any;
  handleUpdateItem: any;
  handleRemoveClientAvatar: () => void;
  handleSelectCustomAvatar: () => void;
  handleVerifyAndSelectCustomAvatar: (e: React.ChangeEvent<HTMLInputElement>) => void;
  configs: any;
  avatarsByUser: any;
  avatarRef: React.RefObject<HTMLInputElement>;
  errors: any;
}

const AvatarSelector: React.FC<AvatarSelectorProps> = ({
  t,
  permissions,
  handleUpdateItem,
  handleRemoveClientAvatar,
  handleSelectCustomAvatar,
  handleVerifyAndSelectCustomAvatar,
  configs,
  avatarsByUser,
  avatarRef,
  errors,
}) => {
  const predefinedAvatars = [
    'https://cdn.1cdn.app/application/LOUISEBOT/2312171433313_Ellipse_980.png',
    'https://cdn.1cdn.app/application/LOUISEBOT/25050417462978_avatar_2.png',
    'https://cdn.1cdn.app/application/LOUISEBOT/2505041746297_avatar_3.png',
    'https://cdn.1cdn.app/application/LOUISEBOT/25050417462958_avatar_4.png',
    'https://cdn.1cdn.app/application/LOUISEBOT/24050807084353_Logo_(2).png',
  ];

  return (
    <div className="tw-self-stretch tw-flex-col tw-justify-start tw-items-start tw-gap-3 tw-flex">
      {/* label */}
      <div className="tw-self-stretch tw-text-gray-700 tw-text-lg tw-font-semibold tw-font-['Lexend'] tw-leading-7">
        {t('DashboardSettingCustomizeChat/t-04')}
        {!permissions.settingsPage.widget.customization.avatar && <ProBadge className="tw-ms-2" />}
      </div>

      {/* content */}
      <div className="tw-self-stretch tw-justify-start tw-items-center tw-gap-6 tw-flex tw-w-full tw-flex-wrap">
        {predefinedAvatars.map(url => (
          <button
            id="btn-avatar-select"
            key={url}
            type="button"
            disabled={!permissions.settingsPage.widget.customization.avatar}
            onClick={() => handleUpdateItem('logo', url)}
            className={`tw-p-0 tw-bg-transparent tw-w-10 tw-h-10 tw-cursor-pointer tw-rounded-full tw-border-4 ${
              configs.logo === url ? 'tw-border-blue-600' : 'tw-border-gray-200'
            } tw-border-solid tw-overflow-hidden`}
          >
            <img className="tw-w-8 tw-h-8 tw-rounded-full tw-object-cover" src={url} alt="" />
          </button>
        ))}

        {avatarsByUser && (
          <div
            className={`tw-w-10 tw-h-10 tw-cursor-pointer tw-rounded-full tw-border-4 tw-relative ${
              typeof configs.logo === 'object' || (!predefinedAvatars.includes(configs.logo) && configs.logo) ? 'tw-border-blue-600' : 'tw-border-gray-200'
            } tw-border-solid`}
          >
            <BiX
              size={22}
              className="tw-text-gray-100 tw-absolute tw-right-[-5px] tw-top-[-10px] tw-cursor-pointer tw-bg-black tw-rounded-full tw-z-10"
              onClick={handleRemoveClientAvatar}
            />
            <button
              id="btn-update-avatar"
              type="button"
              className="tw-p-0 tw-bg-transparent tw-border-0 tw-rounded-full tw-w-full tw-relative"
              disabled={!permissions.settingsPage.widget.customization.avatar}
              onClick={() => {
                if (avatarRef.current?.files && avatarRef.current.files.length > 0) {
                  handleUpdateItem('logo', avatarRef.current.files[0]);
                } else {
                  handleUpdateItem('logo', avatarsByUser);
                }
              }}
            >
              <img
                className="tw-absolute tw-w-full tw-h-9 tw-top-[-20px] tw-left-0 tw-right-0 tw-bottom-0 tw-rounded-full tw-object-cover"
                src={avatarsByUser.toString()}
                alt=""
              />
            </button>
          </div>
        )}

        {/* Image uploader */}
        <button
          id="btn-select-custom-avatar"
          type="button"
          className="tw-pl-3 tw-pr-4 tw-py-2 tw-bg-white tw-rounded-[99px] tw-border tw-border-solid tw-border-gray-300 tw-justify-start tw-items-center tw-gap-2 tw-flex tw-text-gray-700 tw-text-base tw-font-normal tw-font-['Lexend'] tw-leading-normal"
          onClick={handleSelectCustomAvatar}
          disabled={!permissions.settingsPage.widget.customization.avatar}
        >
          <BiSolidImageAdd size={22} className="tw-text-gray-400 tw-mr-2" />
          {t('DashboardSettingCustomizeChat/pop-05')}
        </button>

        <PSInput type="file" hidden ref={avatarRef} accept="image/png, image/jpeg, image/jpg, image/gif" onChange={handleVerifyAndSelectCustomAvatar} />
      </div>

      {/* Validation */}
      {errors.logo && <span className="tw-text-red-500 tw-text-sm tw-block">{errors.logo?.message}</span>}
    </div>
  );
};

export default WrappedErrorBoundary(AvatarSelector);
